/**
 * StrategyStalenessService — Phase V (V4) frontend projection of the backend
 * staleness verdict (builder-service/src/agent/utils/strategyInputManifest.ts).
 *
 * Computes the same three drift channels client-side from Firestore so the Studio
 * UI can show the "inputs drifted" signal without a new backend surface:
 *   1. insights analysis — latest completed ≠ pinned
 *   2. variable set      — project variableSet.currentVersion > pinned
 *   3. tasks             — pinned task fields changed / task deleted
 *
 * Semantics are BINDING (input-versioning.md §4): staleness is a signal, not an
 * error — every lookup degrades to "not drifted" instead of throwing.
 */
import type { CliStrategyStaleness, StrategyInputManifest } from '@modules/shared-types';
import { TaskService } from '@modules/firebase-core/services/TaskService';
import { ProjectService } from '@modules/firebase-core/services/ProjectService';
import { taskConverter } from '@modules/firebase-core/converters/taskConverter';
import { InsightsFirestoreService } from '@modules/insights-analyzer/services/InsightsFirestoreService';

/** Meta keys excluded from task drift comparison — mirror of the backend constant. */
const TASK_DRIFT_IGNORED_KEYS = new Set(['createdAt', 'updatedAt', 'order']);

function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

function taskFieldsDrifted(pinnedTask: Record<string, unknown>, currentDoc: Record<string, unknown>): boolean {
  for (const [key, pinnedValue] of Object.entries(pinnedTask)) {
    if (key === 'id' || TASK_DRIFT_IGNORED_KEYS.has(key)) continue;
    if (canonicalJson(pinnedValue) !== canonicalJson((currentDoc as any)[key])) return true;
  }
  return false;
}

function toMillisAny(value: any): number {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value._seconds === 'number') return value._seconds * 1000;
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (value instanceof Date) return value.getTime();
  return 0;
}

export interface StalenessStrategyInput {
  inputManifest?: StrategyInputManifest;
  sourceData?: { tasks?: Array<Record<string, unknown>> };
}

export class StrategyStalenessService {
  constructor(private readonly projectId: string) {}

  /** Returns null for legacy strategies without an inputManifest (nothing to check). */
  async compute(strategy: StalenessStrategyInput): Promise<CliStrategyStaleness | null> {
    const manifest = strategy.inputManifest;
    if (!manifest) return null;

    let latestCompleted: string | undefined;
    let lineageAware = false;
    let pinnedVersionLabel: string | undefined;
    let latestVersionLabel: string | undefined;
    try {
      const analyses = await new InsightsFirestoreService(this.projectId).list({ limit: 50 });
      const completed = analyses.filter((a) => a.status === 'completed');
      completed.sort((a, b) => toMillisAny((b as any).createdAt) - toMillisAny((a as any).createdAt));
      // Spec D3 (lineage-aware channel): compare within the pinned doc's D2
      // lineage chain when it has one — a NEW UNRELATED analysis must not flag
      // this strategy stale. Pre-D2 pins (no lineageId in the window) keep the
      // bare latest-completed comparison.
      const pinnedDoc = manifest.insightsAnalysisId
        ? completed.find((a) => a.id === manifest.insightsAnalysisId) as (Record<string, unknown> | undefined)
        : undefined;
      const pinnedLineage = typeof pinnedDoc?.lineageId === 'string' ? pinnedDoc.lineageId as string : undefined;
      if (pinnedLineage) {
        const chain = completed.filter((a) => ((a as any).lineageId ?? a.id) === pinnedLineage);
        latestCompleted = chain[0]?.id;
        lineageAware = true;
        pinnedVersionLabel = typeof (pinnedDoc as any)?.versionLabel === 'string' ? (pinnedDoc as any).versionLabel : undefined;
        latestVersionLabel = typeof (chain[0] as any)?.versionLabel === 'string' ? (chain[0] as any).versionLabel : undefined;
      } else {
        latestCompleted = completed[0]?.id;
      }
    } catch { /* signal stays non-drifted */ }
    const analysisDrifted = Boolean(
      latestCompleted && manifest.insightsAnalysisId && latestCompleted !== manifest.insightsAnalysisId,
    );

    let variableSetCurrent: number | undefined;
    try {
      const project = await new ProjectService().get(this.projectId);
      const current = (project as any)?.variableSet?.currentVersion;
      if (typeof current === 'number') variableSetCurrent = current;
    } catch { /* signal stays non-drifted */ }
    const variableSetDrifted = Boolean(
      typeof variableSetCurrent === 'number'
      && typeof manifest.variableSetVersion === 'number'
      && variableSetCurrent > manifest.variableSetVersion,
    );

    const changed: string[] = [];
    const deleted: string[] = [];
    const pinnedTasks = Array.isArray(strategy.sourceData?.tasks) ? strategy.sourceData!.tasks! : [];
    if (pinnedTasks.length > 0) {
      const taskService = new TaskService(this.projectId, taskConverter);
      for (const pinnedTask of pinnedTasks) {
        const taskId = String((pinnedTask as any)?.id ?? '');
        if (!taskId) continue;
        try {
          const current = await taskService.get(taskId);
          if (!current) {
            deleted.push(taskId);
          } else if (taskFieldsDrifted(pinnedTask, current as unknown as Record<string, unknown>)) {
            changed.push(taskId);
          }
        } catch { /* this task stays non-drifted */ }
      }
    }
    const tasksDrifted = changed.length > 0 || deleted.length > 0;

    return {
      stale: analysisDrifted || variableSetDrifted || tasksDrifted,
      insightsAnalysis: {
        ...(manifest.insightsAnalysisId ? { pinned: manifest.insightsAnalysisId } : {}),
        ...(latestCompleted ? { latestCompleted } : {}),
        drifted: analysisDrifted,
        ...(lineageAware ? { lineageAware } : {}),
        ...(pinnedVersionLabel ? { pinnedVersionLabel } : {}),
        ...(latestVersionLabel ? { latestVersionLabel } : {}),
      },
      variableSet: {
        ...(typeof manifest.variableSetVersion === 'number' ? { pinned: manifest.variableSetVersion } : {}),
        ...(typeof variableSetCurrent === 'number' ? { current: variableSetCurrent } : {}),
        drifted: variableSetDrifted,
      },
      tasks: { changed, deleted, drifted: tasksDrifted },
      checkedAt: new Date().toISOString(),
    };
  }
}
